import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import type {
  StudentBillingAdminOverviewDto,
  StudentBillingManualBatchDetailDto,
  StudentBillingManualPreviewDto,
  StudentBillingManualTargetType
} from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchStudentBillingAdminOverview } from "../../shared/api/studentBillingAdmin";
import {
  createStudentBillingManualSource,
  fetchStudentBillingLessonFees,
  fetchStudentBillingManualSource,
  fetchStudentBillingManualSources,
  previewStudentBillingManualSource,
  type StudentBillingLessonFeeListResponse,
  type StudentBillingManualListResponse,
  type StudentBillingManualSourceCapabilities
} from "../../shared/api/studentBillingConfiguration";
import { isUnauthenticatedApiError } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import {
  BillingConfigurationNav,
  BillingStatusBadge,
  billingButtonStyle,
  billingCardStyle,
  billingGridStyle,
  billingInputStyle,
  billingMutedStyle,
  billingPageLinks,
  formatBillingDateTime,
  formatSignedBillingYen,
  studentBillingConfigurationError
} from "./AdminBillingConfigurationShared";

function currentMonth(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit" }).format(new Date());
}

type ManualForm = {
  targetType: StudentBillingManualTargetType;
  targetId: string;
  serviceMonth: string;
  amount: string;
  reason: string;
};

export function AdminBillingManualItemsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const [overview, setOverview] = useState<StudentBillingAdminOverviewDto | null>(null);
  const [lessonFees, setLessonFees] = useState<StudentBillingLessonFeeListResponse | null>(null);
  const [list, setList] = useState<StudentBillingManualListResponse | null>(null);
  const [form, setForm] = useState<ManualForm>({ targetType: "student", targetId: "", serviceMonth: currentMonth(), amount: "", reason: "" });
  const [targetSearch, setTargetSearch] = useState("");
  const [filterMonth, setFilterMonth] = useState(currentMonth());
  const [filterType, setFilterType] = useState<"all" | StudentBillingManualTargetType>("all");
  const [preview, setPreview] = useState<StudentBillingManualPreviewDto | null>(null);
  const [previewCapabilities, setPreviewCapabilities] = useState<StudentBillingManualSourceCapabilities | null>(null);
  const [busy, setBusy] = useState<"idle" | "preview" | "create" | "list">("idle");
  const [safeError, setSafeError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadList = useCallback(async (month = filterMonth, type = filterType) => {
    setBusy("list");
    setSafeError(null);
    try { setList(await fetchStudentBillingManualSources(tenantSlug, month, type === "all" ? null : type)); }
    catch (error) {
      if (isUnauthenticatedApiError(error)) { navigate(`/t/${tenantSlug}/entry`, { replace: true }); return; }
      setSafeError(studentBillingConfigurationError(error, "単発請求・調整の履歴を取得できませんでした。"));
    } finally { setBusy("idle"); }
  }, [filterMonth, filterType, navigate, tenantSlug]);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    let active = true;
    setSafeError(null);
    void Promise.all([
      fetchStudentBillingAdminOverview(tenantSlug),
      fetchStudentBillingLessonFees(tenantSlug),
      fetchStudentBillingManualSources(tenantSlug, currentMonth(), null)
    ]).then(([nextOverview, nextLessons, nextList]) => {
      if (!active) return;
      setOverview(nextOverview);
      setLessonFees(nextLessons);
      setList(nextList);
    }).catch((error: unknown) => {
      if (!active) return;
      if (isUnauthenticatedApiError(error)) { navigate(`/t/${tenantSlug}/entry`, { replace: true }); return; }
      setSafeError(studentBillingConfigurationError(error, "単発請求・調整の設定を取得できませんでした。"));
    });
    return () => { active = false; };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  function changeForm(patch: Partial<ManualForm>) {
    setForm((current) => ({ ...current, ...patch }));
    setPreview(null);
    setPreviewCapabilities(null);
    setMessage(null);
  }

  const targetOptions = useMemo(() => (form.targetType === "student"
    ? (overview?.students.map((student) => ({ id: student.studentId, label: student.displayName })) ?? [])
    : (lessonFees?.items.map((lesson) => ({ id: lesson.lessonId, label: lesson.lessonTitle })) ?? [])
  ).filter((item) => `${item.label} ${item.id}`.toLocaleLowerCase("ja-JP").includes(targetSearch.trim().toLocaleLowerCase("ja-JP"))),
  [form.targetType, lessonFees, overview, targetSearch]);

  function requestValue() {
    const amount = Number(form.amount);
    if (!Number.isSafeInteger(amount) || amount === 0) throw new Error("amount must be signed non-zero integer");
    if (!form.targetId) throw new Error("target is required");
    return {
      target: form.targetType === "student" ? { type: "student" as const, studentId: form.targetId } : { type: "lesson" as const, lessonId: form.targetId },
      serviceMonth: form.serviceMonth,
      amount,
      currency: "JPY" as const,
      reason: form.reason.trim()
    };
  }

  async function runPreview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy("preview");
    setSafeError(null);
    setMessage(null);
    try {
      const response = await previewStudentBillingManualSource(tenantSlug, requestValue());
      setPreview(response.preview);
      setPreviewCapabilities(response.capabilities);
    } catch (error) {
      setPreview(null);
      setPreviewCapabilities(null);
      setSafeError(studentBillingConfigurationError(error, "対象・金額・対象月・理由を確認してください。"));
    } finally { setBusy("idle"); }
  }

  async function create() {
    if (!preview) return;
    setBusy("create");
    setSafeError(null);
    setMessage(null);
    try {
      const batch = await createStudentBillingManualSource(tenantSlug, { ...requestValue(), expectedTargetFingerprint: preview.targetFingerprint });
      setMessage(`単発請求・調整を${batch.targetCount}名分登録しました。`);
      setPreview(null);
      setPreviewCapabilities(null);
      setFilterMonth(batch.serviceMonth);
      setFilterType("all");
      await loadList(batch.serviceMonth, "all");
    } catch (error) {
      setPreview(null);
      setPreviewCapabilities(null);
      setSafeError(studentBillingConfigurationError(error, "登録できませんでした。もう一度プレビューしてください。"));
    } finally { setBusy("idle"); }
  }

  return (
    <OperatorPageScaffold currentArea="admin" tenantSlug={tenantSlug} tenantName={session?.tenant.displayName} session={session}
      title="単発請求・調整" description="生徒またはレッスンを対象に、一度だけ反映する加算・減額を登録します。" links={billingPageLinks} layoutVariant="wide">
      <div style={billingGridStyle}>
        <BillingConfigurationNav tenantSlug={tenantSlug} />
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {safeError ? <p role="alert">{safeError}</p> : null}
        {message ? <p role="status">{message}</p> : null}

        {list && !list.capabilities.canManageManualSources ? <p style={billingMutedStyle}>閲覧のみです。登録できるのはテナントオーナーです。</p> : null}
        {list?.capabilities.canManageManualSources ? (
          <form onSubmit={(event) => void runPreview(event)} style={billingCardStyle}>
            <h2 style={{ margin: 0 }}>単発請求・調整を登録</h2>
            <p style={billingMutedStyle}>プラスは加算、マイナスは減額です。0円は登録できません。</p>
            <label style={billingGridStyle}><span>対象区分</span><select value={form.targetType} onChange={(event) => { setTargetSearch(""); changeForm({ targetType: event.target.value as StudentBillingManualTargetType, targetId: "" }); }} style={billingInputStyle}><option value="student">生徒を指定</option><option value="lesson">レッスンを指定</option></select></label>
            <label style={billingGridStyle}><span>{form.targetType === "student" ? "生徒を検索" : "レッスンを検索"}</span><input type="search" value={targetSearch} onChange={(event) => setTargetSearch(event.target.value)} placeholder="名前で絞り込み" style={billingInputStyle} /></label>
            <label style={billingGridStyle}><span>{form.targetType === "student" ? "生徒" : "レッスン"}</span><select required value={form.targetId} onChange={(event) => changeForm({ targetId: event.target.value })} style={billingInputStyle}><option value="">選択してください</option>{targetOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              <label style={billingGridStyle}><span>対象月</span><input type="month" required value={form.serviceMonth} onChange={(event) => changeForm({ serviceMonth: event.target.value })} style={billingInputStyle} /></label>
              <label style={billingGridStyle}><span>1人あたり金額（円）</span><input aria-label="単発請求・調整金額（円）" type="number" step="1" required value={form.amount} onChange={(event) => changeForm({ amount: event.target.value })} style={billingInputStyle} /></label>
            </div>
            <label style={billingGridStyle}><span>理由（必須）</span><textarea required maxLength={200} rows={3} value={form.reason} onChange={(event) => changeForm({ reason: event.target.value })} style={billingInputStyle} /></label>
            <button type="submit" disabled={busy !== "idle"} style={billingButtonStyle}>{busy === "preview" ? "確認中..." : "対象と金額をプレビュー"}</button>
          </form>
        ) : null}

        {preview ? (
          <section style={{ ...billingCardStyle, borderColor: "#176b55" }} aria-label="登録内容プレビュー">
            <h2 style={{ margin: 0 }}>登録前の最終確認</h2>
            <p style={billingMutedStyle}>サーバーが現在の所属情報から確定した対象です。</p>
            <strong>{preview.sourceLessonTitle ?? preview.targets[0]?.displayName ?? "対象"}</strong>
            <p style={{ margin: 0 }}>対象月: {preview.serviceMonth}</p>
            <p style={{ margin: 0 }}>対象人数: {preview.targetCount}名</p>
            <p style={{ margin: 0 }}>1人あたり: {formatSignedBillingYen(preview.amountPerStudent)}</p>
            <p style={{ margin: 0 }}>合計: {formatSignedBillingYen(preview.totalAmountPreview)}</p>
            <p style={{ margin: 0 }}>理由: {preview.reason}</p>
            <div style={{ maxHeight: 220, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
              {preview.targets.map((target) => <div key={target.studentId}>{target.displayName}</div>)}
            </div>
            <p role="note" style={{ margin: 0, padding: 10, borderRadius: 8, background: "#fff4d8" }}>登録後は編集・削除できません。対象・金額・理由を確認してください。</p>
            <button type="button" disabled={busy !== "idle" || !previewCapabilities?.canManageManualSources} onClick={() => void create()} style={billingButtonStyle}>{busy === "create" ? "登録中..." : "この内容で登録"}</button>
          </section>
        ) : null}

        <section style={billingCardStyle}>
          <h2 style={{ margin: 0 }}>登録履歴</h2>
          <form onSubmit={(event) => { event.preventDefault(); void loadList(); }} style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <label style={billingGridStyle}><span>対象月（必須）</span><input aria-label="履歴対象月" type="month" required value={filterMonth} onChange={(event) => setFilterMonth(event.target.value)} style={billingInputStyle} /></label>
            <label style={billingGridStyle}><span>対象区分</span><select value={filterType} onChange={(event) => setFilterType(event.target.value as typeof filterType)} style={billingInputStyle}><option value="all">すべて</option><option value="student">生徒指定</option><option value="lesson">レッスン指定</option></select></label>
            <button type="submit" disabled={busy !== "idle"} style={billingButtonStyle}>履歴を表示</button>
          </form>
          {list?.items.length === 0 ? <p style={billingMutedStyle}>この月の履歴はありません。</p> : list?.items.map((batch) => (
            <article key={batch.batchId} style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, display: "grid", gap: 4 }}>
              <Link to={`/t/${tenantSlug}/teacher/admin/billing/manual-items/${batch.batchId}`}><strong>{batch.targetType === "lesson" ? `レッスン: ${batch.sourceLessonTitle ?? batch.sourceLessonId}` : `生徒: ${batch.sourceStudentId}`}</strong></Link>
              <span>{formatSignedBillingYen(batch.amountPerStudent)} × {batch.targetCount}名 = {formatSignedBillingYen(batch.totalAmount)}</span>
              <span style={billingMutedStyle}>{batch.reason} / {formatBillingDateTime(batch.createdAt)}</span>
            </article>
          ))}
        </section>
      </div>
    </OperatorPageScaffold>
  );
}

export function AdminBillingManualItemDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", batchId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const [batch, setBatch] = useState<StudentBillingManualBatchDetailDto | null>(null);
  const [safeError, setSafeError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    let active = true;
    void fetchStudentBillingManualSource(tenantSlug, batchId)
      .then((response) => { if (active) setBatch(response.batch); })
      .catch((error: unknown) => {
        if (!active) return;
        if (isUnauthenticatedApiError(error)) { navigate(`/t/${tenantSlug}/entry`, { replace: true }); return; }
        setSafeError(studentBillingConfigurationError(error, "単発請求・調整の詳細を取得できませんでした。"));
      });
    return () => { active = false; };
  }, [batchId, hasAdminRole, isSessionReady, navigate, tenantSlug]);

  return (
    <OperatorPageScaffold currentArea="admin" tenantSlug={tenantSlug} tenantName={session?.tenant.displayName} session={session}
      title="単発請求・調整の詳細" description="登録時に確定した対象と金額を確認します。" links={billingPageLinks} layoutVariant="wide">
      <div style={billingGridStyle}>
        <BillingConfigurationNav tenantSlug={tenantSlug} />
        <Link to={`/t/${tenantSlug}/teacher/admin/billing/manual-items`}>単発請求・調整へ戻る</Link>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {safeError ? <p role="alert">{safeError}</p> : null}
        {batch ? (
          <section style={billingCardStyle}>
            <BillingStatusBadge tone="success">登録済み・変更不可</BillingStatusBadge>
            <h2 style={{ margin: 0 }}>{batch.sourceLessonTitle ?? "生徒個別の調整"}</h2>
            <p style={{ margin: 0 }}>対象: {batch.targetType === "lesson" ? `レッスン（${batch.sourceLessonTitle ?? batch.sourceLessonId}）` : `生徒（${batch.targets[0]?.displayName ?? batch.sourceStudentId}）`}</p>
            <p style={{ margin: 0 }}>対象月: {batch.serviceMonth}</p>
            <p style={{ margin: 0 }}>1人あたり: {formatSignedBillingYen(batch.amountPerStudent)}</p>
            <p style={{ margin: 0 }}>対象人数: {batch.targetCount}名</p>
            <p style={{ margin: 0 }}>合計: {formatSignedBillingYen(batch.totalAmount)}</p>
            <p style={{ margin: 0 }}>理由: {batch.reason}</p>
            <p style={billingMutedStyle}>登録日時: {formatBillingDateTime(batch.createdAt)}</p>
            <h3 style={{ marginBottom: 0 }}>登録時の対象者</h3>
            <div style={{ display: "grid", gap: 4 }}>{batch.targets.map((target) => <span key={target.studentId}>{target.displayName}</span>)}</div>
            <p role="note" style={{ margin: 0, padding: 10, borderRadius: 8, background: "#fff4d8" }}>この登録は監査履歴として不変です。編集・削除はできません。</p>
          </section>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
